import { useRef, useCallback } from 'react'

/**
 * use this when you need to do an action on a node/element from a ref as soon
 * as it's mounted. this hook is similar to what useEffect does; instead, it is
 * how you run actions, subscriptions, and unsubscriptions on ref'd objects.
 *
 * returns the setRef func, which can be passed directly to your ref'd component:
 * e.g.
 * const [cardRef] = useRefCallback({ onNodeMount: (node) => {}, onNodeExit: (node) => {}})
 *
 * <Card ref={cardRef} />
 */
export default function useRefCallback ({
  onNodeMount = () => {},
  onNodeExit = () => {},
}) {
  const ref = useRef(null)
  const setRef = useCallback((node) => {
    if (ref.current) {
      // Make sure to cleanup any events/references added to the last instance
      onNodeExit(ref.current)
    }

    if (node) {
      // Check if a node is actually passed. Otherwise node would be null.
      // You can now do what you need to, addEventListeners, measure, etc.
      onNodeMount(node)
    }

    // Save a reference to the node
    ref.current = node
  }, [])

  return [setRef]
}
